import React, { useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Clock, FilePlus2, Info, Loader2, ShieldCheck, X } from 'lucide-react';
import type { ReportMetadata } from '../../types';
import { describeError } from '../../services/errorMessage';
import { readOnlyTitle } from '../../services/readOnlyNotice';
import { useAbweichungen } from '../../services/projektSteckbrief/useAbweichungen';
import type { AbweichungsStand } from '../../services/projektSteckbrief/abweichungen';
import type { AusgabeUnterlage, FassungsEntscheid } from '../../services/unterlagenAusgabenService';
import {
    abweichungTeile,
    abweichungText,
    abweichungUeberblick,
    begruendungBereinigt,
    entscheidFehler,
    entscheidKennung,
    fassungSatz,
    hinweisAnzeige,
    pruefeBegruendung,
    startetAufgeklappt,
    wertAnfang,
    wertIstLang,
} from './abweichungsHinweisLogik';

/**
 * Hinweis „Diese Unterlage weicht vom Stand der Planungsphase ab" — oben in
 * den Modulen der Folgeunterlagen (Plan Planungsphase, Stufe 6.1: Rückfrage
 * statt Automatik).
 *
 * Der Änderungsdienst (`useAbweichungen`) vergleicht die letzte freigegebene
 * oder versendete Fassung einer Unterlage mit dem heutigen Steckbrief. Wer die
 * Unterlage öffnet, sieht offene Entscheidungen hier — mit denselben drei
 * Knöpfen wie die Rückfrage beim Speichern in der Planungsphase:
 *
 *   Neue Fassung anlegen  — Entscheid speichern; danach öffnet das Modul
 *                           seinen Entwurfsweg (`onNeueFassung`), falls es
 *                           einen hat. Die alte Fassung bleibt gültig, bis die
 *                           neue freigegeben ist.
 *   Nicht erheblich       — mit Pflichtbegründung: der Nachweis, dass der
 *                           Koordinator die Änderung bewertet hat. Für GENAU
 *                           diese Änderung wird nicht erneut gefragt.
 *   Später                — nichts gespeichert; bleibt offen (gelb in Ampel
 *                           und Übergabe), hier nur für diese Sitzung weggelegt.
 *
 * ═══ ZEIGT NUR AN, SCHREIBT NUR PER KLICK ═════════════════════════════════
 * Kein Effekt setzt hier etwas; was gezeigt wird, rechnet `hinweisAnzeige`
 * beim Rendern (geprüft in `abweichungsHinweisLogik.ts`). Geschrieben wird allein
 * über `entscheide` des Dienstes, und nur auf einen Knopf. Das Modul selbst
 * wird nicht angefasst — die Komponente kennt weder sein Formular noch seinen
 * Autospeicher (Falle 3 des Plans: `VorankuendigungView` ist Bug-Magnet).
 *
 * ═══ KEIN RIEGEL FÜR DIE UNTERLAGE ════════════════════════════════════════
 * Der Hinweis sperrt das Modul nicht. Ein Lesefehler des Dienstes erscheint
 * als dezenter Satz „konnte nicht geprüft werden", nie als Fehlerbalken: die
 * Unterlage bleibt bedienbar, „kein Hinweis" darf trotzdem nicht wie „alles
 * geprüft" aussehen — und Stände, die aus Ersatzwerten gerechnet wären, gibt
 * es dann gar nicht (`hinweisAnzeige`, `useAbweichungen`). Der Schreibweg
 * ist bis zum erfolgreichen Lesen gesperrt.
 *
 * ═══ EIN ENTSCHEID WIRD GENAU EINMAL GESPEICHERT ══════════════════════════
 * Der Dienst liest nach dem Erfolg neu; bis die Antwort da ist, zeigt er
 * noch den alten Stand. Ohne Riegel speicherte ein zweiter Klick denselben
 * Entscheid noch einmal und löste den Revisionsweg erneut aus (Codex-Review
 * 17.10.2026). Deshalb zwei Riegel ohne Umweg über das Zeichnen: `laeuftRef`
 * sperrt eine Kennung (Ausgabe-Id + Hash), solange ihr Entscheid unterwegs
 * ist; `erledigt` blendet eine erfolgreich entschiedene Kennung sofort aus
 * und weist jeden weiteren Klick darauf ab.
 *
 * ═══ KNAPP, WENN ES VIEL IST ══════════════════════════════════════════════
 * Der Hinweis steht über der Unterlage und darf sie nicht verdecken. Bis
 * `AUFKLAPP_GRENZE` Zeilen steht die Liste offen; darüber zeigt er nur die
 * Kurzfassung („Baubeschreibung, 141 Fragen") und klappt die Liste auf Wunsch
 * in einem begrenzten, scrollbaren Feld auf. In der offenen Liste steht
 * jeder alte und neue Wert ganz da und bricht um; sehr lange Werte
 * (Baubeschreibung, ab `WERT_KURZ_GRENZE` Zeichen) zeigen ihren Anfang und
 * je Wert einen Knopf „mehr zeigen" — ein Tooltip hilft auf Tablet und Handy
 * nicht (kein Überfahren mit der Maus).
 */

export interface AbweichungsHinweisProps {
    projectId: string;
    metadata: Partial<ReportMetadata> | null;
    /** Die Unterlage dieses Moduls — gezeigt werden nur ihre Stände. */
    unterlage: AusgabeUnterlage;
    isReadOnly: boolean;
    /** Der Entwurfsweg des Moduls — gerufen NACH dem gespeicherten Entscheid „Neue Fassung anlegen". */
    onNeueFassung?: () => void;
    /**
     * Auslöser zum Neulesen (Zähler des Moduls): nach Freigabe-Journal,
     * „Erneut festhalten" und „Neue Revision" hochgezählt, damit der Hinweis
     * die NEUE Fassung vergleicht und nicht die alte (`useAbweichungen`).
     */
    neuladen?: number;
    /**
     * Gesetzt, solange im Modul ein Lauf die Fassung verändert (Freigabe wird
     * festgehalten/abgelegt): die Entscheid-Knöpfe sind dann gesperrt, der
     * Grund steht am Knopf. „Später" bleibt bedienbar.
     */
    sperrgrund?: string | null;
}

const KNOPF = 'inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-md border '
    + 'disabled:opacity-40 disabled:cursor-not-allowed transition';

export const AbweichungsHinweis: React.FC<AbweichungsHinweisProps> = ({
    projectId, metadata, unterlage, isReadOnly, onNeueFassung, neuladen = 0, sperrgrund = null,
}) => {
    const dienst = useAbweichungen(projectId, metadata, neuladen);
    // Waehrend neu gelesen wird, steht noch der alte Stand da — sichtbar, aber gesperrt.
    const sperre = (sperrgrund || '').trim()
        || (dienst.bereit && !dienst.lesefehler && !dienst.entscheidbar ? 'Der Abgleich wird gerade neu gelesen.' : '')
        || null;
    /** Mit „Später" weggelegt — nur für diese Sitzung, gespeichert wird nichts. */
    const [weggelegt, setWeggelegt] = useState<ReadonlySet<string>>(() => new Set());
    /** Kennung des Standes, zu dem gerade die Begründung eingetippt wird. */
    const [begruendungFuer, setBegruendungFuer] = useState<string | null>(null);
    const [begruendung, setBegruendung] = useState('');
    const [begruendungFehler, setBegruendungFehler] = useState<string | null>(null);
    /** Kennung des Standes, dessen Entscheid gerade gespeichert wird — nur für den Spinner. */
    const [laeuft, setLaeuft] = useState<string | null>(null);
    /**
     * Der Riegel selbst: Kennungen (Ausgabe-Id + Hash), deren Entscheid
     * unterwegs ist. Ein Ref, weil `laeuft` als Zustand erst nach dem nächsten
     * Zeichnen greift — ein schneller zweiter Klick käme sonst durch.
     */
    const laeuftRef = useRef<Set<string>>(new Set());
    /** Erfolgreich entschiedene Kennungen — sofort ausgeblendet und gesperrt. */
    const erledigtRef = useRef<Set<string>>(new Set());
    const [erledigt, setErledigt] = useState<ReadonlySet<string>>(() => new Set());
    const [fehler, setFehler] = useState<string | null>(null);
    const [meldung, setMeldung] = useState<string | null>(null);
    /** Stände (`hash`), deren lange Liste aufgeklappt ist — nur Anzeige, nur diese Sitzung. */
    const [aufgeklappt, setAufgeklappt] = useState<ReadonlySet<string>>(() => new Set());
    const umklappen = (hash: string) => setAufgeklappt(prev => {
        const neu = new Set(prev);
        if (neu.has(hash)) neu.delete(hash); else neu.add(hash);
        return neu;
    });
    /** Lange Werte, die ganz gezeigt werden (`hash:zeile:alt|neu`) — nur Anzeige, nur diese Sitzung. */
    const [offeneWerte, setOffeneWerte] = useState<ReadonlySet<string>>(() => new Set());
    const wertUmschalten = (schluessel: string) => setOffeneWerte(prev => {
        const neu = new Set(prev);
        if (neu.has(schluessel)) neu.delete(schluessel); else neu.add(schluessel);
        return neu;
    });

    /** Ein Wert der offenen Liste: kurz ganz, lang mit Anfang und „mehr zeigen" (Knopf statt Tooltip). */
    const wertZeigen = (text: string, schluessel: string) => {
        if (!wertIstLang(text)) return <span>{text}</span>;
        const ganz = offeneWerte.has(schluessel);
        return (
            <span className={ganz ? 'whitespace-pre-line' : undefined}>
                {ganz ? text : wertAnfang(text)}
                <button
                    type="button"
                    onClick={() => wertUmschalten(schluessel)}
                    aria-expanded={ganz}
                    className="ml-1.5 inline-flex items-center px-2 py-0.5 rounded-md border border-slate-200 bg-white text-[11px] font-bold text-slate-600 hover:bg-slate-50 align-baseline transition"
                >
                    {ganz ? 'weniger zeigen' : 'mehr zeigen'}
                </button>
            </span>
        );
    };

    const anzeige = hinweisAnzeige({
        bereit: dienst.bereit,
        lesefehler: dienst.lesefehler,
        staende: dienst.staende,
        unterlage,
        ausgeblendet: weggelegt,
        erledigt,
    });

    /**
     * Speichert einen Entscheid — je Kennung höchstens einmal. Wirft nie nach
     * außen: der Dienst meldet Fehler als Ergebnis (oder, falls doch geworfen
     * wird, fängt das `catch`), und der Satz steht unter dem Hinweis — der
     * Stand bleibt offen. Ein Klick auf eine laufende oder erledigte Kennung
     * tut nichts.
     */
    const entscheide = async (stand: AbweichungsStand, entscheid: FassungsEntscheid, text: string | null): Promise<boolean> => {
        const kennung = entscheidKennung(stand);
        if (laeuftRef.current.has(kennung) || erledigtRef.current.has(kennung)) return false;
        // Zweite Reihe der Sperre: greift auch, wenn der Knopf zwischen Zeichnen
        // und Klick noch aktiv war.
        if (sperre) { setFehler(sperre); return false; }
        laeuftRef.current.add(kennung);
        setLaeuft(stand.hash);
        setFehler(null);
        try {
            const ergebnis = await dienst.entscheide(stand, entscheid, text);
            const satz = entscheidFehler(ergebnis);
            if (satz) { setFehler(satz); return false; }
            // Sofort weg und gesperrt — nicht erst, wenn das Neuladen den Stand entfernt.
            erledigtRef.current.add(kennung);
            setErledigt(new Set(erledigtRef.current));
            return true;
        } catch (e) {
            setFehler(describeError(e));
            return false;
        } finally {
            laeuftRef.current.delete(kennung);
            setLaeuft(null);
        }
    };

    const neueFassung = async (stand: AbweichungsStand) => {
        if (isReadOnly) return;
        const ok = await entscheide(stand, 'neue_fassung', null);
        if (!ok) return;
        setBegruendungFuer(null);
        setMeldung('Entscheid festgehalten: Es wird eine neue Fassung angelegt. Die bisherige Fassung bleibt gültig, bis die neue freigegeben ist.');
        onNeueFassung?.();
    };

    const nichtErheblichStarten = (stand: AbweichungsStand) => {
        if (isReadOnly) return;
        setBegruendungFuer(stand.hash);
        setBegruendung('');
        setBegruendungFehler(null);
    };

    const nichtErheblichSpeichern = async (stand: AbweichungsStand) => {
        if (isReadOnly) return;
        const grund = pruefeBegruendung(begruendung);
        if (grund) { setBegruendungFehler(grund); return; }
        const ok = await entscheide(stand, 'nicht_erheblich', begruendungBereinigt(begruendung));
        if (!ok) return;
        setBegruendungFuer(null);
        setBegruendung('');
        setMeldung('Entscheid festgehalten: nicht erheblich. Für genau diese Änderung wird nicht erneut gefragt.');
    };

    /**
     * „Später": nichts wird gespeichert — der Stand bleibt offen, gelb in
     * Ampel und Übergabe, und der Hinweis kommt beim nächsten Öffnen wieder.
     * Dieselbe Lesart wie im Dialog der Planungsphase (`FassungsRueckfrage`):
     * aufschieben hängt an keinem Netz und ist auch im Lesemodus erlaubt.
     */
    const spaeter = (stand: AbweichungsStand) => {
        setWeggelegt(prev => new Set([...prev, stand.hash]));
        if (begruendungFuer === stand.hash) setBegruendungFuer(null);
    };

    const meldungZeile = meldung && (
        <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-start gap-2 print:hidden">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="flex-1 min-w-0 break-words">{meldung}</span>
            <button
                type="button"
                onClick={() => setMeldung(null)}
                title="Hinweis ausblenden"
                className="shrink-0 p-0.5 rounded hover:bg-black/5 transition"
            >
                <X className="w-3.5 h-3.5" />
            </button>
        </div>
    );

    if (anzeige.art === 'nichts') {
        return meldungZeile ? <div className="mb-4">{meldungZeile}</div> : null;
    }

    if (anzeige.art === 'nicht_geprueft') {
        // Weder Entwarnung noch Abweichung: der Abgleich konnte nicht laufen.
        // Keine Stände, keine Knöpfe — nur der Satz und „Erneut lesen".
        return (
            <div className="mb-4 space-y-2">
                {meldungZeile}
                <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 flex items-start gap-2 print:hidden">
                    <Info className="w-4 h-4 shrink-0 mt-0.5 text-slate-400" />
                    <span className="flex-1 min-w-0 break-words">{anzeige.text}</span>
                    <button
                        type="button"
                        onClick={dienst.neuLaden}
                        className="shrink-0 text-[11px] font-bold text-slate-700 hover:underline"
                    >
                        Erneut lesen
                    </button>
                </div>
            </div>
        );
    }

    const sperrTitel = (was: string) => (isReadOnly ? readOnlyTitle(was) : sperre || undefined);
    const entscheidGesperrt = isReadOnly || !!sperre;

    return (
        // Live-Bereich nur fuer die Meldungszeile: laege er auf der ganzen
        // Section, laese ein Screenreader beim Aufklappen jeden langen Wert vor.
        <section className="mb-4 space-y-2 print:hidden">
            <div aria-live="polite">{meldungZeile}</div>
            <div className="bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-xl shadow-sm">
                <div className="flex items-start gap-2.5 px-4 pt-3">
                    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-800">
                            Diese Unterlage weicht vom Stand der Planungsphase ab
                        </p>
                        <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
                            Ob die Unterlage anzupassen ist, entscheidet der Koordinator — die Fassung bleibt bis dahin gültig.
                        </p>
                    </div>
                </div>

                <ul className="divide-y divide-slate-100">
                    {anzeige.staende.map(stand => {
                        const arbeitet = laeuft === stand.hash;
                        const begruendet = begruendungFuer === stand.hash;
                        const klappbar = !startetAufgeklappt(stand.abweichungen.length);
                        const listeOffen = !klappbar || aufgeklappt.has(stand.hash);
                        const ueberblick = klappbar ? abweichungUeberblick(stand.abweichungen) : '';
                        return (
                            <li key={stand.hash} className="pl-[2.625rem] pr-4 py-2.5 space-y-2">
                                <div className="min-w-0">
                                    <p className="text-[11px] text-slate-700 font-semibold">
                                        {fassungSatz(stand.ausgabe, stand.abweichungen.length)}
                                    </p>
                                    {!listeOffen && (
                                        <p className="text-[11px] text-slate-500 truncate" title={ueberblick}>{ueberblick}</p>
                                    )}
                                </div>

                                {listeOffen && (
                                    <ul className={klappbar
                                        ? 'max-h-64 overflow-y-auto rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 space-y-1'
                                        : 'space-y-0.5'}
                                    >
                                        {stand.abweichungen.map((z, i) => {
                                            const zeile = `${stand.hash}:${i}`;
                                            const t = abweichungTeile(z);
                                            if (!wertIstLang(t.alt) && !wertIstLang(t.neu)) {
                                                return (
                                                    <li key={zeile} className="text-xs text-slate-700 break-words">
                                                        {abweichungText(z)}
                                                    </li>
                                                );
                                            }
                                            return (
                                                <li key={zeile} className="text-xs text-slate-700 break-words">
                                                    {`${t.label}: `}
                                                    {wertZeigen(t.alt, `${zeile}:alt`)}
                                                    {' → '}
                                                    {wertZeigen(t.neu, `${zeile}:neu`)}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}

                                {begruendet && (
                                    <div className="space-y-1">
                                        <label className="block text-[11px] font-bold text-slate-700">
                                            Begründung, warum keine neue Fassung nötig ist
                                            <textarea
                                                value={begruendung}
                                                onChange={e => { setBegruendung(e.target.value); setBegruendungFehler(null); }}
                                                rows={2}
                                                autoFocus
                                                disabled={arbeitet}
                                                placeholder="z. B. Der Kran steht außerhalb des Baufelds; die Vorankündigung nennt ihn nicht."
                                                className="mt-1 w-full p-2 border border-slate-300 rounded-lg text-xs font-normal bg-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                                            />
                                        </label>
                                        {begruendungFehler && (
                                            <p className="text-[11px] text-rose-700">{begruendungFehler}</p>
                                        )}
                                    </div>
                                )}

                                <div className="flex flex-wrap items-center gap-1.5">
                                    {!begruendet && (
                                        <button
                                            type="button"
                                            onClick={() => void neueFassung(stand)}
                                            disabled={entscheidGesperrt || arbeitet}
                                            title={sperrTitel('Ein Entscheid lässt sich nicht festhalten.')
                                                || 'Entscheid festhalten und eine neue Fassung dieser Unterlage beginnen'}
                                            className={`${KNOPF} text-blue-700 bg-white border-blue-300 hover:bg-blue-50`}
                                        >
                                            {arbeitet ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FilePlus2 className="w-3.5 h-3.5" />}
                                            Neue Fassung anlegen
                                        </button>
                                    )}
                                    {begruendet ? (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => void nichtErheblichSpeichern(stand)}
                                                disabled={entscheidGesperrt || arbeitet}
                                                title={sperrTitel('Ein Entscheid lässt sich nicht festhalten.')}
                                                className={`${KNOPF} text-emerald-800 bg-white border-emerald-300 hover:bg-emerald-50`}
                                            >
                                                {arbeitet ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                                                Begründung speichern
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => { setBegruendungFuer(null); setBegruendungFehler(null); }}
                                                disabled={arbeitet}
                                                className={`${KNOPF} text-slate-600 bg-white border-slate-200 hover:bg-slate-50`}
                                            >
                                                Abbrechen
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => nichtErheblichStarten(stand)}
                                            disabled={entscheidGesperrt || arbeitet}
                                            title={sperrTitel('Ein Entscheid lässt sich nicht festhalten.')
                                                || 'Keine neue Fassung — die Änderung mit kurzer Begründung als nicht erheblich bewerten'}
                                            className={`${KNOPF} text-emerald-800 bg-white border-emerald-300 hover:bg-emerald-50`}
                                        >
                                            <ShieldCheck className="w-3.5 h-3.5" /> Nicht erheblich
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => spaeter(stand)}
                                        disabled={arbeitet}
                                        title="Bleibt offen — der Hinweis kommt beim nächsten Öffnen wieder"
                                        className={`${KNOPF} text-slate-600 bg-white border-slate-200 hover:bg-slate-50`}
                                    >
                                        <Clock className="w-3.5 h-3.5" /> Später
                                    </button>
                                    {klappbar && (
                                        <button
                                            type="button"
                                            onClick={() => umklappen(stand.hash)}
                                            aria-expanded={listeOffen}
                                            className="inline-flex items-center gap-1 px-1.5 py-1 text-[11px] font-bold text-slate-500 hover:text-slate-800 transition"
                                        >
                                            {listeOffen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                            {listeOffen ? 'Liste ausblenden' : 'Änderungen anzeigen'}
                                        </button>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>

                {fehler && (
                    <div className="border-t border-slate-100 px-4 py-2">
                        <p className="text-[11px] text-rose-700 flex items-start gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            <span className="min-w-0 break-words">Der Entscheid wurde nicht gespeichert: {fehler}</span>
                        </p>
                    </div>
                )}
            </div>
        </section>
    );
};
